import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { MIGRATION_MODULE_LABELS, useMigration, type MigrationModule } from '@/hooks/useMigration';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import PersonMigrationWizardModal from '@/components/migration/PersonMigrationWizardModal';
import { formatSavedAt, getWizardMissingSteps, getWizardSavedAt, getWizardSnapshot } from '@/lib/migrationCaseWizard';
import { migrationApi } from '@/lib/api';

type ConnectionFormState = {
  host: string;
  port: string;
  database_name: string;
  username: string;
  password: string;
  active: boolean;
};

type CaseFilterStatus = 'all' | 'offen' | 'email_gesendet' | 'onboarding_gestartet' | 'migriert' | 'beendet' | 'fehler';
type WizardFilterStatus = 'all' | 'not_started' | 'open' | 'complete';

const defaultForm: ConnectionFormState = {
  host: '',
  port: '3306',
  database_name: '',
  username: '',
  password: '',
  active: true,
};

const MigrationPage = () => {
  const {
    locations,
    cases,
    dryRunResult,
    resetPreview,
    opsKpis,
    runStatus,
    runLogs,
    isLoading,
    error,
    moduleOptions,
    refreshBaseData,
    saveConnection,
    dryRun,
    startImport,
    loadRun,
    loadCases,
    sendCaseEmail,
    confirmCaseMigration,
    getCaseDetails,
    checkCaseWizard,
    saveCaseWizardDraft,
    previewCaseWizardConfirm,
    loadResetPreview,
    runReset,
    loadOpsKpis,
    selectedConnection,
  } = useMigration();

  const [selectedStandortId, setSelectedStandortId] = useState<number | null>(null);
  const [selectedModules, setSelectedModules] = useState<MigrationModule[]>([...moduleOptions]);
  const [connectionForm, setConnectionForm] = useState<ConnectionFormState>(defaultForm);
  const [currentRunId, setCurrentRunId] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [resetMode, setResetMode] = useState<'migration_only' | 'full_domain'>('migration_only');
  const [resetConfirmText, setResetConfirmText] = useState('');
  // T-0219: Import nur nach Probelauf, Reset nur nach Vorschau — beides mit Rückfrage.
  const [probelaufId, setProbelaufId] = useState<string | null>(null);
  const [importFrage, setImportFrage] = useState(false);
  const [resetFrage, setResetFrage] = useState(false);
  const [caseFilter, setCaseFilter] = useState<CaseFilterStatus>('all');
  const [wizardFilter, setWizardFilter] = useState<WizardFilterStatus>('all');
  const [wizardCaseId, setWizardCaseId] = useState<number | null>(null);
  const [allowContractTermination, setAllowContractTermination] = useState(false);
  const [fachMappingFile, setFachMappingFile] = useState<File | null>(null);
  const [fachMappingResult, setFachMappingResult] = useState<{ written: number; skipped: number; unknown_fach_ids: number[] } | null>(null);
  const [autoSendOnboarding, setAutoSendOnboarding] = useState(false);

  useEffect(() => {
    void refreshBaseData();
  }, [refreshBaseData]);

  useEffect(() => {
    if (!selectedStandortId) return;
    void loadCases(selectedStandortId, caseFilter === 'all' ? undefined : caseFilter);
    void loadOpsKpis(selectedStandortId);
  }, [selectedStandortId, caseFilter, loadCases, loadOpsKpis]);

  useEffect(() => {
    if (!selectedStandortId) return;
    const saved = selectedConnection(selectedStandortId);
    if (!saved) {
      setConnectionForm(defaultForm);
      return;
    }
    setConnectionForm({
      host: saved.host ?? '',
      port: String(saved.port ?? 3306),
      database_name: saved.database_name ?? '',
      username: saved.username ?? '',
      password: '',
      active: !!saved.active,
    });
  }, [selectedStandortId, selectedConnection]);

  useEffect(() => {
    void migrationApi
      .getSettings()
      .then((settings) => setAllowContractTermination(!!settings.allowContractTermination))
      .catch(() => setAllowContractTermination(false));
  }, []);

  useEffect(() => {
    if (!currentRunId) return;
    if (runStatus && ['finished', 'failed', 'cancelled'].includes(runStatus.status)) {
      return; // kein Polling mehr für abgeschlossene Runs
    }
    const interval = globalThis.setInterval(() => {
      void loadRun(currentRunId);
    }, 3000);
    return () => globalThis.clearInterval(interval);
  }, [currentRunId, loadRun, runStatus]);

  const canRun = useMemo(() => selectedStandortId !== null && selectedModules.length > 0, [selectedStandortId, selectedModules]);
  // Ein Probelauf gilt nur für Standort und Bereiche, mit denen er lief.
  useEffect(() => {
    setProbelaufId(null);
  }, [selectedStandortId, selectedModules]);

  const toggleModule = (module: MigrationModule) => {
    setSelectedModules((prev) => {
      if (prev.includes(module)) {
        return prev.filter((m) => m !== module);
      }
      return [...prev, module];
    });
  };

  const onSaveConnection = async () => {
    if (!selectedStandortId) return;
    setIsBusy(true);
    setSuccessMessage(null);
    try {
      await saveConnection(selectedStandortId, {
        host: connectionForm.host.trim(),
        port: Number(connectionForm.port) || 3306,
        database_name: connectionForm.database_name.trim(),
        username: connectionForm.username.trim(),
        password: connectionForm.password.trim() || undefined,
        active: connectionForm.active,
      });
      setConnectionForm((prev) => ({ ...prev, password: '' }));
      setSuccessMessage('Verbindung erfolgreich gespeichert.');
    } finally {
      setIsBusy(false);
    }
  };

  const onDryRun = async () => {
    if (!canRun || !selectedStandortId) return;
    setIsBusy(true);
    setSuccessMessage(null);
    try {
      const result = await dryRun(selectedStandortId, selectedModules);
      setProbelaufId(result?.run_id ?? null);
      if (result?.run_id) {
        setCurrentRunId(result.run_id);
        await loadRun(result.run_id);
      }
      setSuccessMessage('Probelauf abgeschlossen. Der Import ist jetzt 24 Stunden lang möglich.');
    } finally {
      setIsBusy(false);
    }
  };

  const onStartImport = async () => {
    if (!canRun || !selectedStandortId || !probelaufId) return;
    setImportFrage(false);
    setIsBusy(true);
    setSuccessMessage(null);
    try {
      const runId = await startImport(selectedStandortId, selectedModules, probelaufId, autoSendOnboarding);
      setCurrentRunId(runId);
      await loadRun(runId);
      setSuccessMessage(`Import gestartet (Run ${runId}).`);
    } finally {
      setIsBusy(false);
    }
  };

  const onDownloadFachMapping = async () => {
    if (!selectedStandortId) return;
    setIsBusy(true);
    setSuccessMessage(null);
    try {
      const { blob, filename } = await migrationApi.downloadFachMapping(selectedStandortId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setSuccessMessage('Fach-Mapping CSV heruntergeladen.');
    } finally {
      setIsBusy(false);
    }
  };

  const onUploadFachMapping = async () => {
    if (!selectedStandortId || !fachMappingFile) return;
    setIsBusy(true);
    setSuccessMessage(null);
    try {
      const csv = await fachMappingFile.text();
      const result = await migrationApi.uploadFachMappingCsv(selectedStandortId, csv);
      setFachMappingResult(result);
      const unknownNote = result.unknown_fach_ids.length > 0
        ? ` Unbekannte Fach-IDs: ${result.unknown_fach_ids.join(', ')}`
        : '';
      setSuccessMessage(`Fach-Mapping gespeichert: ${result.written} geschrieben, ${result.skipped} übersprungen.${unknownNote}`);
    } finally {
      setIsBusy(false);
    }
  };

  const onLoadResetPreview = async () => {
    if (!selectedStandortId) return;
    setIsBusy(true);
    setSuccessMessage(null);
    try {
      await loadResetPreview(selectedStandortId, resetMode);
      setSuccessMessage('Reset-Vorschau geladen.');
    } finally {
      setIsBusy(false);
    }
  };

  const onSaveMigrationSettings = async () => {
    setIsBusy(true);
    setSuccessMessage(null);
    try {
      const saved = await migrationApi.updateSettings({ allowContractTermination });
      setAllowContractTermination(!!saved.allowContractTermination);
      setSuccessMessage('Migration-Einstellungen gespeichert.');
    } finally {
      setIsBusy(false);
    }
  };

  const vorschauPasst = Boolean(
    resetPreview?.preview_id
      && resetPreview.mode === resetMode
      && Number(resetPreview.standort_id) === Number(selectedStandortId),
  );

  const onRunReset = async () => {
    if (!selectedStandortId || !vorschauPasst || !resetPreview?.preview_id) return;
    setResetFrage(false);
    setIsBusy(true);
    setSuccessMessage(null);
    try {
      await runReset(
        selectedStandortId,
        resetMode,
        resetPreview.preview_id,
        resetMode === 'full_domain' ? resetConfirmText : undefined,
      );
      await Promise.all([
        loadCases(selectedStandortId, caseFilter === 'all' ? undefined : caseFilter),
        loadOpsKpis(selectedStandortId),
        loadResetPreview(selectedStandortId, resetMode),
      ]);
      setSuccessMessage('Reset erfolgreich ausgeführt.');
    } finally {
      setIsBusy(false);
    }
  };

  const onSendCaseEmail = async (caseId: number, resend = false) => {
    setIsBusy(true);
    setSuccessMessage(null);
    try {
      await sendCaseEmail(caseId, resend);
      if (selectedStandortId) {
        await loadCases(selectedStandortId, caseFilter === 'all' ? undefined : caseFilter);
      }
      setSuccessMessage(resend ? 'E-Mail erneut versendet.' : 'E-Mail versendet.');
    } finally {
      setIsBusy(false);
    }
  };

  const onConfirmCaseMigration = async (caseId: number, payload?: Record<string, unknown>) => {
    setIsBusy(true);
    setSuccessMessage(null);
    try {
      const result = await confirmCaseMigration(caseId, payload);
      if (selectedStandortId) {
        await loadCases(selectedStandortId, caseFilter === 'all' ? undefined : caseFilter);
        await loadOpsKpis(selectedStandortId);
      }
      setSuccessMessage('Case als migriert markiert.');
      return result;
    } finally {
      setIsBusy(false);
    }
  };

  const closeWizard = (open: boolean) => {
    if (!open) {
      setWizardCaseId(null);
    }
  };

  const filteredCases = useMemo(() => {
    if (wizardFilter === 'all') return cases;
    return cases.filter((item) => {
      const wizard = getWizardSnapshot(item.snapshot);
      const missingSteps = getWizardMissingSteps(wizard);
      if (wizardFilter === 'not_started') return !wizard;
      if (wizardFilter === 'open') return !!wizard && missingSteps.length > 0;
      if (wizardFilter === 'complete') return !!wizard && missingSteps.length === 0;
      return true;
    });
  }, [cases, wizardFilter]);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Migration</h1>
        <p className="text-muted-foreground">Alt-Standort anbinden, Probelauf prüfen und Import ausführen.</p>
      </div>

      {(error || successMessage) && (
        <div className={error ? 'rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200' : 'rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-200'}>
          {error ?? successMessage}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Standort & Module</CardTitle>
          <CardDescription>Standort auswählen und Importmodule festlegen.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground">Legacy-Standort</span>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2"
                value={selectedStandortId ?? ''}
                onChange={(e) => setSelectedStandortId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Bitte wählen</option>
                {locations.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} (#{s.id})
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="flex flex-wrap gap-2">
            {moduleOptions.map((moduleName) => (
              <button
                type="button"
                key={moduleName}
                onClick={() => toggleModule(moduleName)}
                className={`rounded-full border px-3 py-1 text-xs ${selectedModules.includes(moduleName) ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'}`}
              >
                {MIGRATION_MODULE_LABELS[moduleName] ?? moduleName}
              </button>
            ))}
          </div>
          <div className="rounded-md border border-border/70 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={allowContractTermination}
                  onChange={(e) => setAllowContractTermination(e.target.checked)}
                />
                <span>Button „Vertrag beenden“ in Office anzeigen</span>
              </label>
              <Button size="sm" variant="outline" onClick={onSaveMigrationSettings} disabled={isBusy}>
                Speichern
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Legacy-DB-Verbindung</CardTitle>
          <CardDescription>Zugangsdaten je Standort pflegen (Passwort wird maskiert gespeichert).</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Host</span>
            <input className="w-full rounded-md border border-input bg-background px-3 py-2" value={connectionForm.host} onChange={(e) => setConnectionForm((p) => ({ ...p, host: e.target.value }))} />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Port</span>
            <input className="w-full rounded-md border border-input bg-background px-3 py-2" value={connectionForm.port} onChange={(e) => setConnectionForm((p) => ({ ...p, port: e.target.value }))} />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Datenbank</span>
            <input className="w-full rounded-md border border-input bg-background px-3 py-2" value={connectionForm.database_name} onChange={(e) => setConnectionForm((p) => ({ ...p, database_name: e.target.value }))} />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Benutzer</span>
            <input className="w-full rounded-md border border-input bg-background px-3 py-2" value={connectionForm.username} onChange={(e) => setConnectionForm((p) => ({ ...p, username: e.target.value }))} />
          </label>
          <label className="space-y-1 text-sm md:col-span-2">
            <span className="text-muted-foreground">Passwort (leer lassen, um beizubehalten)</span>
            <input type="password" className="w-full rounded-md border border-input bg-background px-3 py-2" value={connectionForm.password} onChange={(e) => setConnectionForm((p) => ({ ...p, password: e.target.value }))} />
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={connectionForm.active} onChange={(e) => setConnectionForm((p) => ({ ...p, active: e.target.checked }))} />
            <span>Aktiv</span>
          </label>
          <div className="md:col-span-2">
            <Button onClick={onSaveConnection} disabled={!selectedStandortId || isBusy}>
              Verbindung speichern
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Fach-Mapping</CardTitle>
          <CardDescription>
            Voraussetzung für den Lehrer-Import: Legacy-Master-Fach-IDs den neuen fach.id zuordnen.
            Vorlage runterladen, in Excel die Spalte <code>legacy_master_id</code> ausfüllen, dann hochladen.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={onDownloadFachMapping} disabled={!selectedStandortId || isBusy}>
              CSV-Vorlage herunterladen
            </Button>
            <input
              type="file"
              accept=".csv,text/csv"
              className="text-sm"
              onChange={(e) => {
                setFachMappingFile(e.target.files?.[0] ?? null);
                setFachMappingResult(null);
              }}
            />
            <Button onClick={onUploadFachMapping} disabled={!selectedStandortId || !fachMappingFile || isBusy}>
              Mapping hochladen
            </Button>
          </div>
          {fachMappingResult && (
            <div className="rounded-md border border-border p-3 text-sm">
              <div>
                <strong>Geschrieben:</strong> {fachMappingResult.written} &nbsp;|&nbsp;
                <strong>Übersprungen:</strong> {fachMappingResult.skipped}
              </div>
              {fachMappingResult.unknown_fach_ids.length > 0 && (
                <div className="text-amber-300">
                  Unbekannte Fach-IDs (existieren nicht in <code>faecher</code>): {fachMappingResult.unknown_fach_ids.join(', ')}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Probelauf & Import</CardTitle>
          <CardDescription>
            Erst den Probelauf prüfen, danach den Import starten. Ohne Probelauf für diesen Standort und diese
            Bereiche lässt der Server keinen Import zu.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Button variant="outline" onClick={onDryRun} disabled={!canRun || isBusy}>
              Probelauf
            </Button>
            <Button
              onClick={() => setImportFrage(true)}
              disabled={!canRun || isBusy || !probelaufId}
              title={probelaufId ? undefined : 'Erst den Probelauf ausführen'}
            >
              Import starten
            </Button>
          </div>

          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Checkbox
              checked={autoSendOnboarding}
              onCheckedChange={(value) => setAutoSendOnboarding(value === true)}
            />
            Onboarding-E-Mails nach dem Import automatisch versenden
          </label>

          {dryRunResult && (
            <div className="rounded-md border border-border p-3">
              <p className="mb-2 text-sm font-medium">Ergebnis des Probelaufs</p>
              <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                {Object.entries(dryRunResult.modules).map(([moduleName, stats]) => (
                  <div key={moduleName} className="rounded border border-border/80 p-2 text-xs">
                    <div className="mb-1 font-semibold">
                      {MIGRATION_MODULE_LABELS[moduleName as MigrationModule] ?? moduleName}
                    </div>
                    <div>found: {stats.found}</div>
                    <div>eligible: {stats.eligible}</div>
                    <div>will_create: {stats.will_create}</div>
                    <div>will_update: {stats.will_update}</div>
                    <div>new: {stats.new ?? 0}</div>
                    <div>already_mapped: {stats.already_mapped ?? 0}</div>
                    <div>unchanged: {stats.unchanged ?? 0}</div>
                    <div>conflicts: {stats.conflicts}</div>
                    <div>skipped: {stats.skipped}</div>
                    {moduleName === 'teachers' ? (
                      <>
                        <div>imported: {stats.imported ?? 0}</div>
                        <div>reused: {stats.reused ?? 0}</div>
                        <div>ohne aktive Fachzuordnung: {stats.skipped_no_active_fach ?? 0}</div>
                        <div>subjects_linked: {stats.subjects_linked ?? 0}</div>
                        <div>availability_rows: {stats.availability_rows ?? 0}</div>
                      </>
                    ) : moduleName === 'persons' ? (
                      <>
                        <div>cases_found: {stats.cases_found ?? 0}</div>
                        <div>cases_eligible: {stats.cases_eligible ?? 0}</div>
                        <div>cases_open: {stats.cases_open ?? 0}</div>
                        <div>archive_found: {stats.archive_found ?? 0}</div>
                        <div>archive_written: {stats.archive_written ?? 0}</div>
                      </>
                    ) : moduleName === 'rooms' ? (
                      <div className="text-muted-foreground">
                        Raum-Migration: Anlegen/Aktualisieren je Standort inkl. Mapping.
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          )}

          {runStatus && (
            <div className="rounded-md border border-border p-3 text-sm">
              <p>
                <strong>Run:</strong> {runStatus.run_id} | <strong>Status:</strong> {runStatus.status}
              </p>
              {runStatus.started_at && <p><strong>Start:</strong> {runStatus.started_at}</p>}
              {runStatus.finished_at && <p><strong>Ende:</strong> {runStatus.finished_at}</p>}
            </div>
          )}

          {runStatus?.summary?.modules && Object.keys(runStatus.summary.modules).length > 0 && (
            <div className="rounded-md border border-border p-3">
              <p className="mb-2 text-sm font-medium">Fortschritt pro Modul</p>
              <div className="space-y-2">
                {Object.entries(runStatus.summary.modules).map(([moduleName, info]) => {
                  const total = typeof info.total === 'number' ? info.total : 0;
                  const processed = typeof info.processed === 'number' ? info.processed : 0;
                  const moduleStatus = info.status ?? 'pending';
                  const pct = total > 0
                    ? Math.min(100, Math.round((processed / total) * 100))
                    : moduleStatus === 'done' ? 100 : 0;
                  const barColor = moduleStatus === 'failed'
                    ? 'bg-red-500'
                    : moduleStatus === 'done'
                      ? 'bg-emerald-500'
                      : 'bg-primary';
                  return (
                    <div key={moduleName}>
                      <div className="mb-1 flex items-center justify-between text-xs">
                        <span className="font-medium">{moduleName}</span>
                        <span className="text-muted-foreground">
                          {moduleStatus}{total > 0 ? ` — ${processed}/${total} (${pct}%)` : ''}
                        </span>
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded bg-muted">
                        <div className={`h-1.5 ${barColor} transition-[width] duration-300`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {runLogs.length > 0 && (
            <div className="rounded-md border border-border p-3">
              <p className="mb-2 text-sm font-medium">Run-Log ({runLogs.length})</p>
              <div className="max-h-[480px] space-y-2 overflow-auto text-xs">
                {runLogs.map((log) => {
                  const levelClass = log.level === 'error'
                    ? 'border-red-500/40 bg-red-500/10 text-red-200'
                    : log.level === 'warning'
                      ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
                      : 'border-border/70';
                  const hasPayload = log.payload && Object.keys(log.payload).length > 0;
                  return (
                    <div key={log.id} className={`rounded border p-2 ${levelClass}`}>
                      <div>
                        <strong>[{log.level.toUpperCase()}]</strong> {log.module}: {log.message}
                      </div>
                      <div className="text-muted-foreground">{log.created_at}</div>
                      {hasPayload && (
                        <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all rounded bg-background/50 p-2 font-mono text-[10px] text-muted-foreground">
                          {JSON.stringify(log.payload, null, 2)}
                        </pre>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Migrations-Cases</CardTitle>
          <CardDescription>Statusliste und Mailaktionen für Onboarding-Fälle.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={caseFilter}
                onChange={(e) => setCaseFilter(e.target.value as CaseFilterStatus)}
            >
              <option value="all">Alle Status</option>
              <option value="offen">offen</option>
              <option value="email_gesendet">email_gesendet</option>
              <option value="onboarding_gestartet">onboarding_gestartet</option>
              <option value="migriert">migriert</option>
              <option value="beendet">beendet</option>
              <option value="fehler">fehler</option>
            </select>
            <select
              className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={wizardFilter}
              onChange={(e) => setWizardFilter(e.target.value as WizardFilterStatus)}
            >
              <option value="all">Wizard: Alle</option>
              <option value="not_started">Wizard: Nicht gestartet</option>
              <option value="open">Wizard: Offen</option>
              <option value="complete">Wizard: Vollständig</option>
            </select>
          </div>
          <div className="max-h-[520px] overflow-auto rounded border border-border/70">
            <table className="min-w-full text-xs">
              <thead className="sticky top-0 z-10 bg-muted/80 backdrop-blur">
                <tr>
                  <th className="px-2 py-2 text-left">Case</th>
                  <th className="px-2 py-2 text-left">Person</th>
                  <th className="px-2 py-2 text-left">Status</th>
                  <th className="px-2 py-2 text-left">Wizard</th>
                  <th className="px-2 py-2 text-left">Token</th>
                  <th className="px-2 py-2 text-left">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filteredCases.map((item) => {
                  const wizard = getWizardSnapshot(item.snapshot);
                  const missingSteps = getWizardMissingSteps(wizard);
                  const isDone = missingSteps.length === 0;
                  const savedAt = formatSavedAt(getWizardSavedAt(wizard));

                  return (
                    <tr key={item.id} className="border-t border-border/50">
                      <td className="px-2 py-2">#{item.id} / Vertrag {item.legacy_contract_id}</td>
                      <td className="px-2 py-2">{item.wp_user_name ?? item.wp_user_email ?? '-'}</td>
                      <td className="px-2 py-2">{item.status}</td>
                      <td className="px-2 py-2">
                        {!wizard ? (
                          <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-300">
                            Nicht gestartet
                          </span>
                        ) : (
                          <div className="space-y-1">
                            <span
                              className={`rounded-full border px-2 py-1 text-[11px] ${isDone ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' : 'border-red-500/40 bg-red-500/10 text-red-300'}`}
                            >
                              {isDone ? 'Checks vollständig' : `${missingSteps.length} offen`}
                            </span>
                            {savedAt && <div className="text-[11px] text-muted-foreground">Letzter Save: {savedAt}</div>}
                            {!isDone && <div className="text-[11px] text-muted-foreground">Fehlt: {missingSteps.join(', ')}</div>}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2">{item.token_state ?? '-'}</td>
                      <td className="px-2 py-2">
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => onSendCaseEmail(item.id, false)} disabled={isBusy}>
                            Senden
                          </Button>
                          <Button size="sm" onClick={() => onSendCaseEmail(item.id, true)} disabled={isBusy}>
                            Resend
                          </Button>
                          <Button size="sm" variant="secondary" onClick={() => setWizardCaseId(item.id)} disabled={isBusy}>
                            Migration bearbeiten
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredCases.length === 0 && (
                  <tr>
                    <td className="px-2 py-3 text-muted-foreground" colSpan={6}>
                      Keine Cases für die aktuelle Auswahl.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Reset pro Standort</CardTitle>
          <CardDescription>
            Erst die Vorschau laden: Der Reset ist nur mit einer höchstens eine Stunde alten Vorschau für
            denselben Standort und Modus möglich.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground">Reset-Modus</span>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2"
                value={resetMode}
                onChange={(e) => setResetMode(e.target.value as 'migration_only' | 'full_domain')}
              >
                <option value="migration_only">Nur übernommene Daten</option>
                <option value="full_domain">Alle Daten des Standorts (vollständig)</option>
              </select>
            </label>
            {resetMode === 'full_domain' && (
              <label className="space-y-1 text-sm">
                <span className="text-muted-foreground">Bestätigungstext (Standortname)</span>
                <input
                  className="w-full rounded-md border border-input bg-background px-3 py-2"
                  value={resetConfirmText}
                  onChange={(e) => setResetConfirmText(e.target.value)}
                />
              </label>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onLoadResetPreview} disabled={!selectedStandortId || isBusy}>
              Reset-Vorschau laden
            </Button>
            <Button
              variant="destructive"
              onClick={() => setResetFrage(true)}
              disabled={
                !selectedStandortId
                || isBusy
                || !vorschauPasst
                || (resetMode === 'full_domain' && !resetConfirmText.trim())
              }
              title={vorschauPasst ? undefined : 'Erst die Vorschau für diesen Modus laden'}
            >
              Reset ausführen
            </Button>
          </div>

          {resetPreview && (
            <div className="overflow-x-auto rounded border border-border/70">
              <table className="min-w-full text-xs">
                <thead className="bg-muted/30">
                  <tr>
                    <th className="px-2 py-2 text-left">Tabelle</th>
                    <th className="px-2 py-2 text-left">to_delete_count</th>
                    <th className="px-2 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {resetPreview.preview.map((item) => (
                    <tr key={item.table} className="border-t border-border/50">
                      <td className="px-2 py-2">{item.table}</td>
                      <td className="px-2 py-2">{item.to_delete_count}</td>
                      <td className="px-2 py-2">{item.missing ? 'nicht vorhanden' : 'ok'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Funnel / Ops KPIs</CardTitle>
          <CardDescription>Monitoring für Migration-Onboarding je Standort.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
          <div className="rounded border border-border/70 p-3">offen: {opsKpis?.funnel?.offen ?? 0}</div>
          <div className="rounded border border-border/70 p-3">email_gesendet: {opsKpis?.funnel?.email_gesendet ?? 0}</div>
          <div className="rounded border border-border/70 p-3">onboarding_gestartet: {opsKpis?.funnel?.onboarding_gestartet ?? 0}</div>
          <div className="rounded border border-border/70 p-3">migriert: {opsKpis?.funnel?.migriert ?? 0}</div>
          <div className="rounded border border-border/70 p-3">beendet: {opsKpis?.funnel?.beendet ?? 0}</div>
          <div className="rounded border border-border/70 p-3">fehler: {opsKpis?.funnel?.fehler ?? 0}</div>
          <div className="rounded border border-border/70 p-3">Archiv: {opsKpis?.archive_count ?? 0}</div>
          <div className="rounded border border-border/70 p-3">Median Abschluss (min): {opsKpis?.median_completion_minutes ?? '-'}</div>
        </CardContent>
      </Card>

      {isLoading && <p className="text-sm text-muted-foreground">Lade Migrationsdaten...</p>}

      <Dialog open={importFrage} onOpenChange={setImportFrage}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import starten?</DialogTitle>
            <DialogDescription>
              Übernommen werden: {selectedModules.map((m) => MIGRATION_MODULE_LABELS[m] ?? m).join(', ')}.
              {autoSendOnboarding ? ' Danach gehen die Registrierungsmails automatisch raus.' : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportFrage(false)}>Abbrechen</Button>
            <Button onClick={() => void onStartImport()} disabled={isBusy}>Import starten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resetFrage} onOpenChange={setResetFrage}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset wirklich ausführen?</DialogTitle>
            <DialogDescription>
              Gelöscht wird, was die Vorschau zeigt (
              {(resetPreview?.preview ?? []).reduce((summe, z) => summe + Number(z.to_delete_count || 0), 0)}{' '}
              Einträge). Das lässt sich nicht rückgängig machen.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetFrage(false)}>Abbrechen</Button>
            <Button variant="destructive" onClick={() => void onRunReset()} disabled={isBusy}>Reset ausführen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PersonMigrationWizardModal
        open={wizardCaseId !== null}
        caseId={wizardCaseId}
        onOpenChange={closeWizard}
        onCompleted={async () => {
          if (selectedStandortId) {
            await Promise.all([
              loadCases(selectedStandortId, caseFilter === 'all' ? undefined : caseFilter),
              loadOpsKpis(selectedStandortId),
            ]);
          }
          setSuccessMessage('Case erfolgreich über Wizard migriert.');
        }}
        getCaseDetails={getCaseDetails}
        checkCaseWizard={checkCaseWizard}
        saveCaseWizardDraft={saveCaseWizardDraft}
        previewCaseWizardConfirm={previewCaseWizardConfirm}
        confirmCaseMigration={onConfirmCaseMigration}
      />
    </div>
  );
};

export default MigrationPage;
